import { observer } from 'mobx-react-lite'
import { Box, Button, Divider, Label, PhoneWithLabelInput } from 'shared/ui'
import { TextField } from 'shared/ui/TextField'
import { LINK_CALL_FORWARDING_GUIDE } from 'shared/constants/links'
import { type IResponseNumber } from 'entities/Phone/api/types'
import { TextToSpeechContext } from 'widgets/TextToSpeech/context/context'
import { TextToSpeechText } from 'widgets/TextToSpeech/ui/TextToSpeechText/TextToSpeechText'
import { TextToSpeechInfo } from 'widgets/TextToSpeech/ui/TextToSpeechInfo/TextToSpeechInfo'
import { PlaySpeechBtn } from 'widgets/TextToSpeech/ui/TextToSpeechActions/PlaySpeechBtn/PlaySpeechBtn'
import { MergeFieldBtn } from 'widgets/TextToSpeech/ui/TextToSpeechActions/MergeFieldBtn/MergeFieldBtn'
import { type CallForwardingStore } from 'pages/settings/pages/inboxes/pages/id/pages/numbers/store/callForwardingStore'
import { DoubleOptInSettingLayout } from 'pages/settings/pages/doubleOptIn/ui/doubleOptInSetting/DoubleOptInSettingLayout'
import styles from './styles.module.scss'

type ICallForwardingModalContentProps = {
  store: CallForwardingStore
  number: IResponseNumber
}

export const CallForwardingModalContent = observer(
  ({ store, number }: ICallForwardingModalContentProps) => {
    const {
      callForwardingEnabled,
      callForwardingExtension,
      callWhisperEnabled,
      callScreeningEnabled,
      closeCallForwardingModal,
      toggleCallForwarding,
      setCallForwardingExtension,
      toggleCallWhisper,
      toggleCallScreening,
      loadingSaveCallForwarding,
      isCallForwardingSaveDisabled,
      textToSpeechStore,
      saveCallForwarding,
      phoneInputStore,
    } = store

    return (
      <div>
        <Box display={'flex'} flexDirection={'column'} gap={'24px'}>
          <DoubleOptInSettingLayout
            title={'Call forwarding status'}
            description={
              'When enabled, all calls to your Salesmsg number will be forwarded to the number provided.'
            }
            checked={callForwardingEnabled}
            onToggle={toggleCallForwarding}
          />

          {callForwardingEnabled && (
            <>
              <Box display={'flex'} gap={'12px'} alignItems={'flex-start'}>
                <Box flex={1} style={{ marginTop: '-20px' }}>
                  <PhoneWithLabelInput
                    label={'Phone number'}
                    item={phoneInputStore.country}
                    items={phoneInputStore.countries}
                    loading={loadingSaveCallForwarding}
                    number={phoneInputStore.number}
                    numberError={phoneInputStore.numberError}
                    isValidNumber={phoneInputStore.isValidNumber}
                    numberRequestError={phoneInputStore.numberRequestError}
                    setNumber={phoneInputStore.setNumber}
                    setCountry={phoneInputStore.setCountry}
                    triggerNumberValidation={phoneInputStore.triggerNumberValidation}
                  />
                </Box>
                <Box width={'100px'}>
                  <Label
                    label={{
                      text: 'Extension',
                      className: styles.label,
                    }}
                  />
                  <TextField
                    variant='stroke'
                    size='medium'
                    value={callForwardingExtension}
                    onChange={setCallForwardingExtension}
                    InputProps={{
                      placeholder: '123',
                    }}
                    ariaLabel={'CallForwardingExtension'}
                  />
                </Box>
              </Box>

              <Box marginBottom={'16px'} marginTop={'10px'}>
                <DoubleOptInSettingLayout
                  title={'Call Whisper'}
                  description={
                    'A short message that plays before the call. The caller does not hear this message.'
                  }
                  checked={callWhisperEnabled}
                  onToggle={toggleCallWhisper}
                />

                {callWhisperEnabled && textToSpeechStore && (
                  <Box display={'flex'} flexDirection={'column'} gap={'8px'}>
                    <TextToSpeechContext.Provider value={textToSpeechStore}>
                      <Box display={'flex'} flexDirection={'column'} gap={'8px'}>
                        <div className={styles.textToSpeechWrapper}>
                          <TextToSpeechText className={styles.textToSpeechText} />
                          <Box
                            display={'flex'}
                            alignItems={'center'}
                            padding={'0 12px'}
                            gap={'4px'}
                          >
                            <MergeFieldBtn />
                            <TextToSpeechInfo className={styles.textToSpeechInfo} />
                          </Box>
                          <Box padding={'0 12px 12px'}>
                            <Divider style={{ marginTop: '2px', marginBottom: '14px' }} />
                            <PlaySpeechBtn />
                          </Box>
                        </div>
                      </Box>
                    </TextToSpeechContext.Provider>
                  </Box>
                )}
                {!callWhisperEnabled && (
                  <Divider style={{ marginTop: '8px', marginBottom: '8px' }} />
                )}
                <DoubleOptInSettingLayout
                  title={'Call Screening'}
                  description={
                    'When enabled, you will be prompted to accept the call when answering the forwarded call.'
                  }
                  learnMoreLink={LINK_CALL_FORWARDING_GUIDE}
                  checked={callScreeningEnabled}
                  onToggle={toggleCallScreening}
                />
              </Box>
            </>
          )}
        </Box>
        <div className={styles.actions}>
          <Button
            text='Cancel'
            contained='tertiary'
            size='medium'
            onClick={closeCallForwardingModal}
          />
          <Button
            text={number.call_forwarding?.is_active ? 'Save changes' : 'Save'}
            typeBtn='contained'
            size='medium'
            loading={loadingSaveCallForwarding}
            disabled={isCallForwardingSaveDisabled}
            onClick={() => saveCallForwarding(number.id)}
          />
        </div>
      </div>
    )
  }
)
